import { useMutation } from "@apollo/client";
import { getRouteApi } from "@tanstack/react-router";
import { Fragment, useRef, useState } from "react";
import { LotsTable, supplyChainLotQuery } from "../LotsTable";
import { weightedAveragesQuery } from "../WeightedAverages";
import { BenchmarkFilters } from "@/benchmarks/BenchmarkFilters";
import { LotForm } from "@/benchmarks/LotForm";
import { type LotFormSchema } from "@/benchmarks/LotForm/validation";
import { ExportLotsButton } from "@/components/AsyncJobMenu/components/ExportLotsButton";
import { PageHeader } from "@/components/PageHeader";
import { type ResetPagination } from "@/components/Pagination/usePagination";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { graphql } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { nullable } from "@/lib/utils/common";
import { toISO } from "@/lib/utils/dates";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";
import { transformForApi } from "@/lib/utils/transforms";

const routeApi = getRouteApi("/(authed)/_layout/benchmarks");

export function Benchmarks() {
  const [isOpen, setIsOpen] = useState(false);
  const openTableRef = useRef<{ resetPagination: ResetPagination }>(null);
  const closedTableRef = useRef<{ resetPagination: ResetPagination }>(null);
  const filters = routeApi.useSearch();
  const { toast } = useToast();
  const { activeBusinessUnitId } = useActiveBusinessUnit();

  const [createSupplyChainLot] = useMutation(createSupplyChainLotMutation, {
    refetchQueries: [supplyChainLotQuery, weightedAveragesQuery],
  });

  const handleResetPagination = () => {
    openTableRef.current?.resetPagination();
    closedTableRef.current?.resetPagination();
  };

  const onSubmit = async (values: LotFormSchema) => {
    try {
      if (!activeBusinessUnitId) {
        toast({
          title: "Error",
          description: "Missing active business unit.",
          variant: "destructive",
        });
        return;
      }

      const result = await createSupplyChainLot({
        variables: {
          input: {
            businessUnit: activeBusinessUnitId,
            sexClass: values.sexClass,
            status: values.status,
            reference: transformForApi.text(values.reference),
            sortNote: nullable(transformForApi.text(values.sortNote || "")),
            sourcePartner: nullable(values.sourcePartner),
            destinationPartner: nullable(values.destinationPartner),
            animalBreeds: values.animalBreeds?.length ? values.animalBreeds : [],
            weightTotalIn: values.weightTotalIn ? values.weightTotalIn : null,
            weightTotalOut: values.weightTotalOut ? values.weightTotalOut : null,
            weightTotalFeedIntake: values.weightTotalFeedIntake ? values.weightTotalFeedIntake : null,
            purchasePrice: values.purchasePrice ? values.purchasePrice : null,
            salePrice: values.salePrice ? values.salePrice : null,
            feedCost: values.feedCost ? values.feedCost : null,
            nonFeedCost: values.nonFeedCost ? values.nonFeedCost : null,
            primeFraction: values.primeFraction ? String(Number(values.primeFraction) / 100) : null,
            choiceFraction: values.choiceFraction ? String(Number(values.choiceFraction) / 100) : null,
            selectFraction: values.selectFraction ? String(Number(values.selectFraction) / 100) : null,
            numTotalIn: values.numTotalIn ? Number(values.numTotalIn) : null,
            numTotalOut: values.numTotalOut ? Number(values.numTotalOut) : null,
            numTotalDead: values.numTotalDead ? Number(values.numTotalDead) : null,
            numTotalRemoved: values.numTotalRemoved ? Number(values.numTotalRemoved) : null,
            dateOpened: values.dateOpened ? toISO(values.dateOpened) : null,
            dateClosed: values.dateClosed ? toISO(values.dateClosed) : null,
          },
        },
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: "Error creating lot",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });
        return;
      }

      const mutationResult = result.data?.createSupplyChainLot;
      if (!mutationResult?.success || (mutationResult?.errors?.length ?? 0) > 0) {
        toast({
          title: "Error creating lot",
          description: mutationResult?.errors?.length
            ? extractErrorMessages(mutationResult.errors)
            : "The operation failed. Please try again.",
          variant: "destructive",
        });
        return;
      }

      setIsOpen(false);

      toast({
        title: "Success",
        description: `Lot "${values.reference}" created.`,
        variant: "default",
      });
    } catch (error) {
      console.error("[LotCreate] Unexpected error:", error);
      toast({
        title: "Error creating lot",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const onCancel = () => {
    setIsOpen(false);
  };

  return (
    <Fragment>
      <PageHeader>
        <div className="w-full flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <PageHeader.Title>Benchmarks</PageHeader.Title>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <ExportLotsButton filters={filters} className="w-full sm:w-auto" />
            <Button variant="confirm" onClick={() => setIsOpen(true)} className="w-full sm:w-auto">
              Create Lot
            </Button>
          </div>
        </div>
      </PageHeader>
      <BenchmarkFilters handleResetPagination={handleResetPagination} />
      <LotsTable tableId="closedLotsTable" status="CLOSED" ref={closedTableRef} filters={filters} />
      <LotsTable tableId="openLotsTable" status="OPEN" ref={openTableRef} filters={filters} />

      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent className="max-w-[800px] max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Create Lot</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">Create Lot Form</AlertDialogDescription>
          </AlertDialogHeader>
          <LotForm onSubmit={onSubmit} onCancel={onCancel} />
        </AlertDialogContent>
      </AlertDialog>
    </Fragment>
  );
}

export const createSupplyChainLotMutation = graphql(`
  mutation CreateSupplyChainLot($input: CreateSupplyChainLotInput!) {
    createSupplyChainLot(input: $input) {
      success
      errors {
        field
        message
      }
      supplyChainLot {
        id
        reference
        sexClass
        status
      }
    }
  }
`);
